import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { BackgroundJobsService } from '../../../backgroundJob/types/BackgroundJobs';
import { Databases } from '../../../catalog/resources/Databases';
import { Tables } from '../../../catalog/resources/Tables';
import { Messages } from '../../../codegen/Localize';
import { DataSources } from '../../../content/resources/Datasources';
import { Workbooks } from '../../../content/resources/Workbooks';
import { Flows } from '../../../flow/resources/Flows';
import { Metrics } from '../../../metrics/resources/Metrics';
import { Projects } from '../../../projects/resources/Projects';
import { PublishedConnections } from '../../../publishedConnection/resources/PublishedConnections';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { DataRoles } from '../../../ts/resources/Dataroles';
import { Lenses } from '../../../ts/resources/Lenses';
import { IActionResource } from '../../../ts/resources/Resource';
import { CustomViews } from '../../../ts/ServerApi/CustomViews/CustomViews';
import { ContentDraftStatus } from '../../../ts/ServerApi/types/ContentDraft';
import { Views } from '../../../views/resources/Views';
import { AvailableActionsForEntityRequest } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import { availableActionForEntityRequestSucceeded, AvailableActionsForEntityRequestAction } from '../../actions/spaces/AvailableActionsForEntityActions';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { DataConnections } from '~src/connectivity/resources/DataConnections';
import { getCommonDatasourceInfo } from '../../spaces/CommonDatasourceInfo';
import { AvailableActions } from '../../state/AvailableActionsForEntityState';
import { SpaceInfo } from '../../state/SpaceInfo';
import { getCorrectEntityIdForActions } from './SpacesSagaHelpers';

const GetActionResourceForContentType: Partial<Record<ExplorableItemType, () => IActionResource<AvailableActions>>> = {
  workbook: () => Workbooks,
  project: () => Projects,
  datasource: () => DataSources,
  dataRole: () => DataRoles,
  lens: () => Lenses,
  view: () => Views,
  dataconnection: () => DataConnections,
  flow: () => Flows,
  backgroundJob: () => BackgroundJobsService,
  table: () => Tables,
  tableWithCrass: () => Tables,
  database: () => Databases,
  databaseWithCrass: () => Databases,
  metric: () => Metrics,
  publishedConnection: () => PublishedConnections,
  customView: () => CustomViews
};

function getResourceContentId(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo,
): string {
  if (entityType === 'extractTask') {
    return spaceInfo.item.id;
  }
  return entityId;
}

function getResourceContentType(
  entityType: ExplorableItemType,
  spaceInfo: SpaceInfo
): ExplorableItemType {
  if (entityType === 'extractTask') {
    return spaceInfo.item.contentType as ExplorableItemType;
  }
  return entityType;
}

/**
 * We request actions only for the ExplorableItemTypes that have an API call.
 * For other types there aren't APIs that retrieve the allowed actions;
 * instead, we determine allowed actions by looking at properties of those items and the site role.
 */
export function fetchAvailableActionsForEntity(
  entityType: ExplorableItemType,
  entityId: string,
  spaceInfo: SpaceInfo
): ng.IPromise<AvailableActions> {
  const resourceContentId = getResourceContentId(entityType, entityId, spaceInfo);
  const resourceContentType = getResourceContentType(entityType, spaceInfo);
  const actionResourceGetter = GetActionResourceForContentType[resourceContentType];
  const actionResource = actionResourceGetter && actionResourceGetter();
  if (actionResource) {
    return actionResource.actions(resourceContentId);
  }
  return null;
}

function fetchAvailableActionsForFlowDraft(draftId: string): ng.IPromise<AvailableActions> {
  return Flows.draftActions(draftId);
}


export function* processAvailableActionsForEntityRequest(action: AvailableActionsForEntityRequestAction) {
  try {
    const spaceInfo = yield select(selectSpaceInfo);
    const commonDatasource = getCommonDatasourceInfo(action.item as IContentInfo<'datasource'>);

    if (action.item.contentType === 'datasource' && DataSources.isEmbedded(commonDatasource)) {
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, null));
    } else if (action.item.contentType === 'flow'
      && (action.item as IContentInfo<'flow'>).draftStatus === ContentDraftStatus.unpublished) {
      // flow drafts that have never been published need to call getFlowDraftActions instead of getFlowActions
      const availableActions = yield call(fetchAvailableActionsForFlowDraft, action.item.id);
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
    } else {
      let entityIdForActions = yield call(getCorrectEntityIdForActions, action.item.contentType, action.item.id);

      // Hack to use `vizportalId` field for Database & Table
      // TFS912863: Remove this hack once we switched to use vizportalId as primary ID for database & table
      if (action.item.contentType === 'table' ||
          action.item.contentType === 'tableWithCrass' ||
          action.item.contentType === 'database' ||
          action.item.contentType === 'databaseWithCrass') {
        entityIdForActions = action.item.vizportalId;
      }

      const availableActions = yield call(fetchAvailableActionsForEntity, action.item.contentType, entityIdForActions,
          spaceInfo);
      yield put(availableActionForEntityRequestSucceeded(action.item.contentType, action.item.id, availableActions));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootAvailableActionsForEntitySaga(): SagaIterator {
  yield takeLatest(AvailableActionsForEntityRequest, processAvailableActionsForEntityRequest);
}
